import { Button, MenuItem, TextField } from "@material-ui/core";
import React, { useCallback, useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import { getNotificationHistory } from "../../../../api/notification";
import { getUserMessages, markUserMessageAsRead } from "../../../../api/message";
import { alertError } from "../../../../utils/logger";
import { useSkeleton } from "../../../hooks/useSkeleton";

const tabStyle = (selected) => ({
  color: selected ? "#321ACB" : "#7E8299",
  borderBottom: selected ? "2px solid #321ACB" : "2px solid transparent",
});

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const levelColor = (level) => {
  const value = String(level || "").toLowerCase();
  if (value.includes("error")) return { background: "#FFE2E5", color: "#F64E60" };
  if (value.includes("advertencia")) return { background: "#FFF4DE", color: "#FFA800" };
  if (value.includes("exito")) return { background: "#E8FFF3", color: "#0BB783" };
  return { background: "#EAF4FF", color: "#3699FF" };
};

export default function MessagingPage() {
  const [selectedTab, setSelectedTab] = useState("notificaciones");
  const [history, setHistory] = useState({ items: [], total: 0, pagina: 1, limite: 25 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(false);
  const [messageHistory, setMessageHistory] = useState({ items: [], total: 0, pagina: 1, limite: 25 });
  const [messageStatus, setMessageStatus] = useState("no_leidos");
  const [messagePage, setMessagePage] = useState(1);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const loggedUser = useSelector((store) => store.authentication?.user, shallowEqual);
  const accessToken = loggedUser?.accessToken;
  const { isLoading, disableLoading, ContentSkeleton } = useSkeleton();

  const loadHistory = useCallback(async (showSkeleton = false) => {
    if (!showSkeleton) setLoading(true);
    try {
      const response = await getNotificationHistory(accessToken, page, limit);
      if (response.status === 200) setHistory(response.data);
    } catch (error) {
      alertError({ error, customMessage: "No se ha podido obtener el histórico de notificaciones." });
    } finally {
      setLoading(false);
      if (showSkeleton) disableLoading();
    }
  }, [accessToken, disableLoading, limit, page]);

  useEffect(() => {
    loadHistory(true);
  }, [loadHistory]);

  const loadMessages = useCallback(async () => {
    setMessagesLoading(true);
    try {
      const response = await getUserMessages(accessToken, messageStatus, messagePage, limit);
      if (response.status === 200) setMessageHistory(response.data);
    } catch (error) {
      alertError({ error, customMessage: "No se han podido obtener los mensajes." });
    } finally {
      setMessagesLoading(false);
    }
  }, [accessToken, limit, messagePage, messageStatus]);

  useEffect(() => {
    if (selectedTab === "mensajes") loadMessages();
  }, [loadMessages, selectedTab]);

  const totalPages = Math.max(1, Math.ceil((history.total || 0) / limit));
  const messageTotalPages = Math.max(1, Math.ceil((messageHistory.total || 0) / limit));

  const markAsRead = async (messageId) => {
    try {
      await markUserMessageAsRead(messageId, accessToken);
      await loadMessages();
    } catch (error) {
      alertError({ error, customMessage: "No se ha podido marcar el mensaje como leído." });
    }
  };

  if (isLoading) return <ContentSkeleton />;

  return (
    <>
      <Card>
        <CardHeader title="Mensajería" />
        <CardBody>
          <div className="d-flex border-bottom" style={{ gap: "1.5rem" }}>
            <button type="button" className="border-0 bg-transparent font-weight-bolder pb-3" onClick={() => setSelectedTab("notificaciones")} style={tabStyle(selectedTab === "notificaciones")}>Notificaciones</button>
            <button type="button" className="border-0 bg-transparent font-weight-bolder pb-3" onClick={() => setSelectedTab("mensajes")} style={tabStyle(selectedTab === "mensajes")}>Mensajes</button>
          </div>
        </CardBody>
      </Card>

      {selectedTab === "notificaciones" ? (
        <Card className="mt-5">
          <CardHeader title="Histórico de notificaciones">
            <CardHeaderToolbar>
              <TextField select size="small" variant="outlined" value={limit} onChange={(event) => { setPage(1); setLimit(Number(event.target.value)); }}>
                {[25, 50, 100].map((value) => <MenuItem key={value} value={value}>{value} por página</MenuItem>)}
              </TextField>
              <Button className="ml-3" variant="outlined" color="primary" disabled={loading} onClick={() => loadHistory(false)}>{loading ? "Actualizando..." : "Actualizar"}</Button>
            </CardHeaderToolbar>
          </CardHeader>
          <CardBody>
            <div className="text-muted mb-5">Consulta de las acciones registradas por la aplicación, los usuarios y los agentes. Total: {history.total || 0}.</div>
            <div className="table-responsive">
              <table className="table table-hover table-vertical-center">
                <thead><tr><th>Fecha</th><th>Nivel</th><th>Acción</th><th>Detalle</th><th>Origen</th></tr></thead>
                <tbody>
                  {!history.items?.length ? <tr><td colSpan="5" className="text-center text-muted py-8">No hay notificaciones registradas.</td></tr> : history.items.map((notification) => (
                    <tr key={notification.id}>
                      <td className="text-nowrap">{formatDate(notification.fecha)}</td>
                      <td><span className="label label-inline font-weight-bolder" style={levelColor(notification.nivel)}>{notification.nivel || "Información"}</span></td>
                      <td><div className="font-weight-bolder">{notification.titulo}</div><div className="text-muted small">{notification.tipo_evento || "-"}</div></td>
                      <td style={{ minWidth: "280px" }}>{notification.mensaje || "-"}</td>
                      <td>{notification.agente_nombre || notification.actor_tipo || "Sistema"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="d-flex align-items-center justify-content-between pt-3">
              <div className="text-muted">Página {page} de {totalPages}</div>
              <div>
                <Button variant="outlined" disabled={page <= 1 || loading} onClick={() => setPage((current) => current - 1)}>Anterior</Button>
                <Button className="ml-3" variant="outlined" color="primary" disabled={page >= totalPages || loading} onClick={() => setPage((current) => current + 1)}>Siguiente</Button>
              </div>
            </div>
          </CardBody>
        </Card>
      ) : (
        <Card className="mt-5">
          <CardHeader title="Mensajes">
            <CardHeaderToolbar>
              <TextField select size="small" variant="outlined" value={messageStatus} onChange={(event) => { setMessagePage(1); setMessageStatus(event.target.value); }}>
                <MenuItem value="no_leidos">No leídos</MenuItem>
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="leidos">Leídos</MenuItem>
              </TextField>
              <Button className="ml-3" variant="outlined" color="primary" disabled={messagesLoading} onClick={loadMessages}>{messagesLoading ? "Actualizando..." : "Actualizar"}</Button>
            </CardHeaderToolbar>
          </CardHeader>
          <CardBody>
            <div className="text-muted mb-5">Mensajes importantes dirigidos a tu usuario. Total: {messageHistory.total || 0}.</div>
            {!messageHistory.items?.length ? <div className="text-center text-muted py-8">No hay mensajes para el filtro seleccionado.</div> : <div className="d-flex flex-column" style={{ gap: "12px" }}>
              {messageHistory.items.map((message) => <div key={message.id} className="border rounded p-4" style={{ background: message.leido ? "#FFF" : "#F3F6F9" }}>
                <div className="d-flex justify-content-between align-items-start" style={{ gap: "16px" }}><div><div className="font-weight-bolder text-dark">{message.titulo}</div><div className="text-muted small mt-1">{formatDate(message.fecha)} · {message.tipo_evento}</div></div>{!message.leido && <Button size="small" variant="outlined" color="primary" onClick={() => markAsRead(message.id)}>Marcar como leído</Button>}</div>
                <div className="mt-3">{message.mensaje}</div>
              </div>)}
            </div>}
            <div className="d-flex align-items-center justify-content-between pt-5"><div className="text-muted">Página {messagePage} de {messageTotalPages}</div><div><Button variant="outlined" disabled={messagePage <= 1 || messagesLoading} onClick={() => setMessagePage((current) => current - 1)}>Anterior</Button><Button className="ml-3" variant="outlined" color="primary" disabled={messagePage >= messageTotalPages || messagesLoading} onClick={() => setMessagePage((current) => current + 1)}>Siguiente</Button></div></div>
          </CardBody>
        </Card>
      )}
    </>
  );
}
